import { logger } from 'utils/logger';

export type AuthMode = 'keycloak' | 'legacy';

export type AuthModeOptions = {
  /** Delay before each retry; the number of entries is the number of retries */
  retryDelaysMs: number[];
  /** Each settings request is aborted after this long */
  attemptTimeoutMs: number;
};

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

async function fetchAuthMode(timeoutMs: number): Promise<AuthMode> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(
      `${import.meta.env['VITE_LEGACY_LOGIN_API']}/keycloak/settings`,
      { signal: controller.signal },
    );

    if (!response.ok) {
      throw new Error(`status ${response.status}`);
    }

    // A bot-manager challenge page or any other non-JSON body throws here
    const body: unknown = await response.json();
    const keycloakAuth = (body as { keycloakAuth?: unknown } | null)
      ?.keycloakAuth;

    if (typeof keycloakAuth !== 'boolean') {
      throw new Error('settings have no keycloakAuth flag');
    }

    return keycloakAuth ? 'keycloak' : 'legacy';
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Reads the Auth mode from `/keycloak/settings`. Legacy applies only when the
 * settings say so explicitly; settings that cannot be read mean Keycloak.
 */
export async function resolveAuthMode({
  retryDelaysMs,
  attemptTimeoutMs,
}: AuthModeOptions): Promise<AuthMode> {
  const errors: string[] = [];

  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
    if (attempt > 0) {
      await wait(retryDelaysMs[attempt - 1]);
    }

    try {
      return await fetchAuthMode(attemptTimeoutMs);
    } catch (error) {
      errors.push(String(error));
    }
  }

  logger.error('[session] /keycloak/settings unreadable, assuming Keycloak', {
    errors,
  });

  return 'keycloak';
}
